import { useCallback, useEffect, useMemo, useState } from 'react';

import { $GradeLevel, GradeLevel } from '@suntex.fim/enums';
import { getFlags } from '@suntex.fim/flags';
import { useSessionData } from '@suntex.fim/framework';

import { PlayerIndexResponse } from '../context/types';
import { getFocusOptions } from '../utils/focus';

export const NO_FOCUS = -1;

export type SelectedFocus = GradeLevel | typeof NO_FOCUS;

export function useFocus({
  playerInfo,
}: {
  playerInfo: PlayerIndexResponse | null;
}) {
  const sessionData = useSessionData<{
    playerFocus?: SelectedFocus;
    playerFocusTeamId?: number;
  }>();
  const flags = getFlags();
  const focusSelectorEnabled = flags.player.focusSelector;
  const [focus, _setFocus] = useState<SelectedFocus>(NO_FOCUS);
  const isTeacher = Boolean(playerInfo?.player.isTeacher);
  const teamId = playerInfo?.team?.teamId;
  const setFocus = useCallback(
    (focus: SelectedFocus) => {
      sessionData.set('playerFocus', focus);
      if (isTeacher) {
        if (teamId !== undefined) {
          sessionData.set('playerFocusTeamId', teamId);
        } else {
          sessionData.remove('playerFocusTeamId');
        }
      } else {
        sessionData.remove('playerFocusTeamId');
      }
      _setFocus(focus);
    },
    [isTeacher, sessionData, teamId],
  );

  // Memoize focus options to avoid recomputing
  const focusOptions = useMemo(
    () => (playerInfo ? getFocusOptions(playerInfo) : $GradeLevel.all()),
    [playerInfo],
  );

  // Update focus when playerInfo loads or changes
  useEffect(() => {
    if (!focusSelectorEnabled) {
      // If the focus selector is disabled, we don't want to set any focus
      return;
    }

    const storedTeamId = sessionData.get('playerFocusTeamId');
    const storedFocus =
      !isTeacher || (teamId !== undefined && storedTeamId === teamId)
        ? sessionData.get('playerFocus')
        : undefined;

    if (storedFocus === NO_FOCUS) {
      _setFocus(storedFocus);
    } else {
      const grade = isTeacher
        ? (playerInfo?.team?.grade ?? playerInfo?.player.grade)
        : playerInfo?.player.grade;
      const defaultFocus = $GradeLevel.fromOptional(grade) ?? NO_FOCUS;
      const selectedFocus =
        storedFocus !== undefined && focusOptions.includes(storedFocus)
          ? storedFocus
          : defaultFocus !== NO_FOCUS && focusOptions.includes(defaultFocus)
            ? defaultFocus
            : NO_FOCUS;
      setFocus(selectedFocus);
    }
  }, [
    focusOptions,
    focusSelectorEnabled,
    isTeacher,
    playerInfo?.player.grade,
    playerInfo?.team?.grade,
    sessionData,
    setFocus,
    teamId,
  ]);

  return { focus, setFocus, focusOptions };
}
